"use client";

import { Suspense, useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "next/navigation";
import { useT } from "@/lib/i18n";
import type { GuideSummary } from "@/lib/api/types";
import { cachedFetch } from "@/lib/fetch-cache";
import { Link } from "@/i18n/navigation";
import SearchFilter from "@/app/components/SearchFilter";
import { useBetaPrefix } from "@/lib/api/prefix.client";
import { replaceQuery } from "@/lib/replace-query";

const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

const difficultyColors: Record<string, string> = {
  beginner: "bg-success/10 text-success border-success/40",
  intermediate: "bg-warning/10 text-warning border-warning/40",
  advanced: "bg-danger/10 text-danger border-danger/40",
};

const categoryLabels: Record<string, string> = {
  general: "General",
  character: "Character",
  strategy: "Strategy",
  mechanic: "Mechanic",
  boss: "Boss",
  event: "Event",
  advanced: "Advanced",
};

const categoryOptions = Object.entries(categoryLabels).map(
  ([value, label]) => ({ label, value }),
);

const difficultyOptions = [
  { label: "Beginner", value: "beginner" },
  { label: "Intermediate", value: "intermediate" },
  { label: "Advanced", value: "advanced" },
];

const sortOptions = [
  { label: "Newest", value: "newest" },
  { label: "Oldest", value: "oldest" },
  { label: "A → Z", value: "az" },
  { label: "Z → A", value: "za" },
];

function GuidesClientInner({
  initialGuides,
}: {
  initialGuides: GuideSummary[];
}) {
  const t = useT();
  const bp = useBetaPrefix();
  const searchParams = useSearchParams();
  const [guides, setGuides] = useState<GuideSummary[]>(initialGuides);
  const [search, setSearch] = useState(searchParams.get("search") || "");

  // Client-side fallback if server-side fetch failed
  useEffect(() => {
    if (initialGuides.length > 0) return;
    cachedFetch<GuideSummary[]>(`${API}/api/guides`)
      .then(setGuides)
      .catch(() => {});
  }, [initialGuides]);
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [difficulty, setDifficulty] = useState(
    searchParams.get("difficulty") || "",
  );
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");

  const updateUrl = useCallback((newState: Record<string, string>) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(newState)) {
      if (v && v !== "newest") params.set(k, v);
    }
    const qs = params.toString();
    replaceQuery(qs);
  }, []);

  const setFilterAndUrl = useCallback(
    (key: string, value: string, setter: (v: string) => void) => {
      setter(value);
      const current: Record<string, string> = {
        search,
        category,
        difficulty,
        sort,
      };
      current[key] = value;
      updateUrl(current);
    },
    [search, category, difficulty, sort, updateUrl],
  );

  const filtered = useMemo(() => {
    let result = [...guides];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (g) =>
          g.title.toLowerCase().includes(q) ||
          g.summary.toLowerCase().includes(q) ||
          g.author.toLowerCase().includes(q) ||
          g.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    if (category) result = result.filter((g) => g.category === category);
    if (difficulty) result = result.filter((g) => g.difficulty === difficulty);

    if (sort === "newest") result.sort((a, b) => b.date.localeCompare(a.date));
    else if (sort === "oldest")
      result.sort((a, b) => a.date.localeCompare(b.date));
    else if (sort === "az")
      result.sort((a, b) => a.title.localeCompare(b.title));
    else if (sort === "za")
      result.sort((a, b) => b.title.localeCompare(a.title));

    return result;
  }, [guides, search, category, difficulty, sort]);

  return (
    <>
      <SearchFilter
        search={search}
        onSearchChange={(v) => setFilterAndUrl("search", v, setSearch)}
        placeholder={t("Search guides...")}
        resultCount={filtered.length}
        sortOptions={sortOptions.map((o) => ({ ...o, label: t(o.label) }))}
        sortValue={sort}
        onSortChange={(v) => setFilterAndUrl("sort", v, setSort)}
        filters={[
          {
            label: "Any Category",
            name: "Category",
            value: category,
            options: categoryOptions,
            onChange: (v) => setFilterAndUrl("category", v, setCategory),
          },
          {
            label: "Any Difficulty",
            name: "Difficulty",
            value: difficulty,
            options: difficultyOptions,
            onChange: (v) => setFilterAndUrl("difficulty", v, setDifficulty),
          },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((guide) => (
          <Link
            key={guide.slug}
            href={`${bp}/guides/${guide.slug}`}
            className="bg-[var(--bg-card)] rounded-lg border border-[var(--border-subtle)] p-5 hover:bg-[var(--bg-card-hover)] hover:border-[var(--border-accent)] transition-all block"
          >
            <h3 className="font-semibold text-lg text-[var(--accent-gold)] mb-1">
              {guide.title}
            </h3>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-3 line-clamp-2">
              {guide.summary}
            </p>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-[var(--text-muted)]">
                {t("by {author}", { author: guide.author })}
              </span>
              <span className="text-[var(--text-muted)]">&middot;</span>
              <span className="text-[var(--text-muted)]">{guide.date}</span>
              <span
                className={`px-2 py-0.5 rounded border text-[10px] font-medium ${difficultyColors[guide.difficulty] || "bg-surface text-fg-muted border-line-strong"}`}
              >
                {guide.difficulty}
              </span>
              <span className="px-2 py-0.5 rounded bg-[var(--bg-primary)] text-[var(--text-muted)] border border-[var(--border-subtle)] text-[10px]">
                {t(categoryLabels[guide.category] || guide.category)}
              </span>
              {guide.tags
                .filter(
                  (tag) =>
                    tag.toLowerCase() !== guide.difficulty &&
                    tag.toLowerCase() !== guide.category,
                )
                .map((tag) => (
                  <span
                    key={tag}
                    className="px-1.5 py-0.5 rounded bg-[var(--bg-primary)] text-[var(--text-muted)] text-[10px]"
                  >
                    {tag}
                  </span>
                ))}
            </div>
          </Link>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-[var(--text-muted)]">
          {t("No guides found matching your filters.")}
        </div>
      )}
    </>
  );
}

// useSearchParams needs a Suspense boundary above it now that the root
// layout no longer provides one (the app-wide boundary made every dynamic
// page's body invisible to non-JS crawlers). The boundary lives here so
// every page that renders this client, English and localized, gets it.
export default function GuidesClient(
  props: Parameters<typeof GuidesClientInner>[0],
) {
  return (
    <Suspense fallback={null}>
      <GuidesClientInner {...props} />
    </Suspense>
  );
}
